import { get } from "@lib/apiClient";
import { deriveRankingYear, getImageUrl, parseMarkdown, replacePlaceholders } from "@lib/utils";
import type {
    DescriptionSection,
    MetaTags,
    RankingDescriptionResponse,
    RankingsMeta,
    UniversitiesRankingResponse,
} from "@lib/types";
import type { RecentlyPublishedArticle } from "@repo/js-components/types";
import { mapRecentlyPublishedArticles } from "@repo/js-components/lib";
import { type InstitutionRankingHit, meilisearchSearch } from "./meilisearch";

export type RankingFilters = {
    countryCode?: string;
    page?: number;
    rankingType?: string;
};

export type UniversityRanking = {
    data: ReturnType<typeof mapInstitutionHit>[];
    isSpecial: boolean;
    pagination: RankingDescriptionResponse["pagination"];
    relatedArticles: RecentlyPublishedArticle[];
};

async function fetchRankingMetadata(
    disciplineSlug: string,
    filters: RankingFilters,
): Promise<UniversitiesRankingResponse | null> {
    const params: Record<string, string> = {
        rankingType: filters.rankingType || "ALL",
    };

    if (filters.countryCode) {
        params.countryCode = filters.countryCode;
    }

    const endpoint = `/v2/rankings/universities/disciplines/${disciplineSlug}`;

    try {
        return await get<UniversitiesRankingResponse>(endpoint as `/${string}`, params);
    } catch (error) {
        console.error(`Error with endpoint ${endpoint}:`, error);
        return null;
    }
}

function fetchInstitutionsFromMeilisearch(disciplineSlug: string, filters: RankingFilters) {
    const meilisearchFilters: string[] = [`discipline.slug = "${disciplineSlug}"`];
    if (filters.countryCode) {
        meilisearchFilters.push(`country.code = "${filters.countryCode}"`);
    }

    return meilisearchSearch<InstitutionRankingHit>("institution-ranking-items", {
        q: "",
        filter: meilisearchFilters.join(" AND "),
        sort: ["worldPosition:asc"],
        page: filters.page || 1,
        hitsPerPage: 100,
        attributesToRetrieve: [
            "institution.labDevId", "institution.name", "institution.slug",
            "country.code", "country.name",
            "worldPosition", "nationalPosition",
            "hIndex", "publications", "citations", "scholars",
        ],
    });
}

function mapInstitutionHit(hit: InstitutionRankingHit) {
    return {
        worldPosition: hit.worldPosition,
        nationalPosition: hit.nationalPosition,
        dIndex: hit.hIndex,
        publications: hit.publications,
        numberOfScientists: hit.scholars,
        university: {
            labDevId: hit.institution.labDevId,
            name: hit.institution.name,
            slug: hit.institution.slug,
            country: hit.country.name,
            countryCode: hit.country.code,
            isHidden: false,
        },
        thumbnail: getImageUrl(hit.institution.labDevId, "institutions", 64),
    };
}

export async function getUniversitiesRankingRecentlyPublishedArticles(
    disciplineSlug: string,
    countryCode?: string | null,
) {
    const countryCodeParam = countryCode ?? "";
    const articles = await get<RecentlyPublishedArticle[]>(
        `/v2/rankings/universities/recent-articles/disciplines/${disciplineSlug}/countries/${countryCodeParam}`,
    );
    return mapRecentlyPublishedArticles(articles);
}

export async function getUniversityRankingComplete(disciplineSlug: string, filters: RankingFilters) {
    const [metadata, meilisearchResult] = await Promise.all([
        fetchRankingMetadata(disciplineSlug, filters),
        fetchInstitutionsFromMeilisearch(disciplineSlug, filters),
    ]);

    if (!metadata) {
        return {
            ranking: null,
            description: null,
        };
    }

    const pagination = {
        pageNumber: meilisearchResult.page,
        pageSize: meilisearchResult.hitsPerPage,
        total: meilisearchResult.totalHits,
    };

    const ranking: UniversityRanking = {
        data: meilisearchResult.hits.map(mapInstitutionHit),
        isSpecial: metadata.isSpecial,
        pagination,
        relatedArticles: mapRecentlyPublishedArticles(metadata.relatedArticles ?? []),
    };

    const descriptionResponse: RankingDescriptionResponse = {
        meta: {
            disciplineName: metadata.disciplineName,
            country: metadata.country?.name || null,
            countryCode: metadata.country?.slug || null,
            year: deriveRankingYear(metadata.editionDate),
        },
        pagination,
        description: {
            content: metadata.description || "",
        },
    };

    const description = mapRankingDescription(descriptionResponse);

    return {
        ranking,
        description,
    };
}

function mapRankingDescription(ranking: RankingDescriptionResponse) {
    return {
        meta: ranking.meta,
        pagination: ranking.pagination,
        descriptionContentSections: buildDescriptionSections(parseMarkdown(ranking.description.content || "")),
        ...buildUniversitiesRanking(ranking),
    };
}

/**
 * Builds universities ranking metadata
 * @param ranking - Ranking description response
 * @returns Universities ranking metadata
 */
function buildUniversitiesRanking(ranking: RankingDescriptionResponse): RankingsMeta {
    const title = buildUniversitiesTitle(ranking);
    const metaTags = buildUniversitiesMetaTags(ranking);

    return { title, metaTags };
}

/**
 * Builds universities ranking title
 * @param ranking - Ranking description response
 * @returns Universities ranking title
 */
function buildUniversitiesTitle(ranking: RankingDescriptionResponse) {
    const {
        meta: { disciplineName, country, year },
    } = ranking;

    let rankingTitle = isSpecialRanking(disciplineName) ? `Best Universities` : `Best ${disciplineName} Universities`;
    if (country) {
        rankingTitle += ` in ${country}`;
    } else {
        rankingTitle += ` in the World`;
    }

    rankingTitle += ` ${year}`;

    if (isSpecialRanking(disciplineName)) {
        rankingTitle += ` Ranking`;
    }

    return rankingTitle;
}

/**
 * Builds universities ranking meta tags
 * @param ranking - Ranking description response
 * @returns Universities ranking meta tags
 */
function buildUniversitiesMetaTags(ranking: RankingDescriptionResponse): MetaTags {
    const isSpecial = isSpecialRanking(ranking.meta.disciplineName);

    let baseTitle = isSpecial
        ? `Best Universities in the World RANKING_YEAR Ranking | Research.com`
        : `Best DISCIPLINE_NAME Universities in the World Ranking RANKING_YEAR | Research.com`;
    let baseDescription = isSpecial
        ? `Compare the best universities in the world for RANKING_YEAR. Discover Research.com annual ranking of universities worldwide.`
        : `Compare the best DISCIPLINE_NAME universities in the world for RANKING_YEAR. Discover Research.com annual DISCIPLINE_NAME ranking of universities worldwide.`;

    if (ranking.meta.country) {
        baseTitle = isSpecial
            ? `Best Universities in COUNTRY_NAME RANKING_YEAR Ranking | Research.com`
            : `Best DISCIPLINE_NAME University Ranking in COUNTRY_NAME RANKING_YEAR | Research.com`;
        baseDescription = isSpecial
            ? `Compare the best universities in COUNTRY_NAME for RANKING_YEAR. Discover Research.com annual ranking of universities in COUNTRY_NAME.`
            : `Compare the best DISCIPLINE_NAME universities in COUNTRY_NAME for RANKING_YEAR. Discover Research.com annual DISCIPLINE_NAME ranking of universities in COUNTRY_NAME.`;
    }

    return buildMetaTags(ranking, baseTitle, baseDescription);
}

/**
 * Builds meta tags from a ranking description response
 * @param ranking - Ranking description response
 * @param baseTitle - Base title template
 * @param baseDescription - Base description template
 * @returns Meta tags
 */
function buildMetaTags(ranking: RankingDescriptionResponse, baseTitle: string, baseDescription: string): MetaTags {
    const {
        meta: { disciplineName, year, country },
    } = ranking;

    const replacements = {
        DISCIPLINE_NAME: disciplineName,
        RANKING_YEAR: `${year || ""}`,
        COUNTRY_NAME: country || "",
    };
    const title = replacePlaceholders(baseTitle, replacements);
    const description = replacePlaceholders(baseDescription, replacements);

    return {
        title,
        description,
    };
}

/**
 * Builds description sections from a description string
 * @param description - Description string
 * @returns Description sections
 */
export function buildDescriptionSections(description: string): DescriptionSection | null {
    if (!description) {
        return null;
    }

    const paragraphs = description.split("</p>");
    if (paragraphs.length > 1) {
        const firstParagraph = `${paragraphs[0]}</p>`;
        const restParagraphs = paragraphs
            .slice(1)
            .map(p => p.trim())
            .filter(p => p);

        if (restParagraphs.length > 0) {
            const rest = `${restParagraphs.join("</p>")}</p>`;
            return {
                firstParagraph: firstParagraph.trim(),
                rest: rest.trim(),
            };
        }
    }

    return {
        firstParagraph: description.trim(),
        rest: "",
    };
}

export function isSpecialRanking(disciplineName: string) {
    return ["best universities"].includes(disciplineName.toLowerCase());
}
